import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ImageOff, Search, X, ListChecks, CircleSlash, ChevronRight } from 'lucide-react';
import ShimmerImage from '../../components/ShimmerImage';
import SearchableDropdown from '../../components/SearchableDropdown';
import {
  hardwareApi, CATEGORY_LABEL, CATEGORY_CHIP,
  type DeviceModelRow,
} from '../../services/hardwareApi';
import { RubyLoader } from '../../components/RubyLoader';

/**
 * PROVISIONING — STEP 1: PICK THE DEVICE.
 *
 * The provisioning tab is two steps: this gallery, then that device's checklist
 * editor. A dropdown alone made the tab feel like it was editing "the"
 * template; a picture wall makes it obvious there is one checklist PER DEVICE,
 * and which devices still have none.
 *
 * The step count per model is its own GET — there is no bulk templates
 * endpoint — so the cards render immediately and each badge fills in as its
 * request lands, a few at a time. A slow or failing count never blocks
 * picking a device.
 */

export interface TemplateSummary { categories: number; steps: number }

const CONCURRENCY = 6;

const ProvisioningDevicePicker: React.FC<{
  models: DeviceModelRow[];
  loading: boolean;
  onPick: (modelId: string) => void;
}> = ({ models, loading, onPick }) => {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [brand, setBrand] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [summaries, setSummaries] = useState<Record<string, TemplateSummary | null>>({});
  const cancelled = useRef(false);

  const thumbOf = (m: DeviceModelRow) => m.media?.heroImageUrl || m.media?.photos?.[0];

  // Fetch every model's checklist size, a few at a time. `cancelled` stops the
  // queue on unmount so a tab switch mid-sweep does not keep firing requests.
  useEffect(() => {
    cancelled.current = false;
    if (models.length === 0) return;
    const queue = models.map((m) => m.modelId);
    let i = 0;
    const worker = async () => {
      while (!cancelled.current && i < queue.length) {
        const mid = queue[i++];
        try {
          const res = await hardwareApi.getProvisioningTemplate(mid);
          if (cancelled.current) return;
          setSummaries((prev) => ({
            ...prev,
            [mid]: {
              categories: (res.template.categories || []).length,
              steps: (res.template.steps || []).length,
            },
          }));
        } catch {
          // A count that cannot be read is shown as unknown, never as zero —
          // "no checklist" is a claim this screen should not make on a failure.
          if (!cancelled.current) setSummaries((prev) => ({ ...prev, [mid]: null }));
        }
      }
    };
    Array.from({ length: Math.min(CONCURRENCY, queue.length) }, worker);
    return () => { cancelled.current = true; };
  }, [models]);

  const brands = useMemo(
    () => Array.from(new Set(models.map((m) => m.brand || m.manufacturer).filter(Boolean) as string[])).sort(),
    [models],
  );
  const categories = useMemo(
    () => Array.from(new Set(models.map((m) => m.category).filter(Boolean))).sort(),
    [models],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return models
      .filter((m) => {
        if (category && m.category !== category) return false;
        if (brand && (m.brand || m.manufacturer) !== brand) return false;
        if (status) {
          const s = summaries[m.modelId];
          // Unknown counts stay visible under both filters rather than
          // disappearing into the wrong bucket.
          if (s) {
            if (status === 'has' && s.steps === 0) return false;
            if (status === 'none' && s.steps > 0) return false;
          }
        }
        if (!q) return true;
        return [m.modelName, m.brand, m.manufacturer, m.modelId, m.subCategory]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));
      })
      .sort((a, b) => a.modelName.localeCompare(b.modelName));
  }, [models, search, category, brand, status, summaries]);

  const withChecklist = models.filter((m) => (summaries[m.modelId]?.steps ?? 0) > 0).length;
  const filtersOn = !!(search || category || brand || status);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="min-w-[15rem] flex-1">
          <label className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-stone-500">Find a device</label>
          <div className="flex min-h-[44px] items-center gap-2 rounded-lg border border-gray-300 bg-white px-3">
            <Search size={14} className="shrink-0 text-stone-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, brand or device ID…"
              className="flex-1 bg-transparent py-2 text-sm text-stone-900 placeholder-stone-400"
              style={{ outline: 'none', boxShadow: 'none', border: 'none' }}
            />
            {search && (
              <button onClick={() => setSearch('')} className="p-0.5 text-stone-400 hover:text-stone-700"><X size={14} /></button>
            )}
          </div>
        </div>
        <div className="w-44">
          <label className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-stone-500">Category</label>
          <SearchableDropdown
            options={categories.map((c) => ({ value: c, label: CATEGORY_LABEL[c] || c }))}
            value={category}
            onChange={setCategory}
            placeholder="All categories"
            variant="light"
          />
        </div>
        <div className="w-44">
          <label className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-stone-500">Brand</label>
          <SearchableDropdown
            options={brands.map((b) => ({ value: b, label: b }))}
            value={brand}
            onChange={setBrand}
            placeholder="All brands"
            searchPlaceholder="Search brands…"
            variant="light"
          />
        </div>
        <div className="w-44">
          <label className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-stone-500">Checklist</label>
          <SearchableDropdown
            options={[
              { value: 'has', label: 'Has a checklist' },
              { value: 'none', label: 'Not set up yet' },
            ]}
            value={status}
            onChange={setStatus}
            placeholder="Any"
            variant="light"
          />
        </div>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-stone-500">
        <span><b className="font-mono text-stone-800">{filtered.length}</b> device{filtered.length === 1 ? '' : 's'} shown</span>
        <span><b className="font-mono text-emerald-700">{withChecklist}</b> with a checklist</span>
        <span><b className="font-mono text-amber-600">{models.length - withChecklist}</b> still to set up</span>
        {filtersOn && (
          <button
            onClick={() => { setSearch(''); setCategory(null); setBrand(null); setStatus(null); }}
            className="font-semibold text-red-600 hover:underline"
          >
            Clear filters
          </button>
        )}
      </div>

      {loading ? (
        <RubyLoader variant="section" />
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-stone-300 bg-white py-16 text-center">
          <CircleSlash size={26} className="mx-auto mb-3 text-stone-300" />
          <p className="text-sm font-medium text-stone-700">
            {models.length === 0 ? 'No device models yet' : 'No device matches those filters'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filtered.map((m) => {
            const s = summaries[m.modelId];
            const pending = !(m.modelId in summaries);
            return (
              <button
                key={m.modelId}
                onClick={() => onPick(m.modelId)}
                className="group flex flex-col overflow-hidden rounded-xl border border-stone-200 bg-white text-left transition-colors hover:border-red-300 hover:shadow-sm"
              >
                <div className="border-b border-stone-100 bg-stone-50 p-3">
                  <ShimmerImage
                    src={thumbOf(m)}
                    alt={m.modelName}
                    width="100%"
                    height={130}
                    objectFit="contain"
                    className="rounded-lg border border-stone-200 bg-white"
                    fallbackBgColor="bg-white"
                    fallbackIcon={<ImageOff size={22} className="text-stone-300" />}
                  />
                </div>
                <div className="flex flex-1 flex-col p-3">
                  <div className="truncate text-sm font-bold text-stone-900">{m.modelName}</div>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${CATEGORY_CHIP[m.category] || 'bg-stone-100 text-stone-600'}`}>
                      {CATEGORY_LABEL[m.category] || m.category}
                    </span>
                    <span className="truncate text-[11px] text-stone-500">{m.brand || m.manufacturer}</span>
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-2 border-t border-stone-100 pt-2.5">
                    {pending ? (
                      <RubyLoader variant="inline" size={14} label="Checking…" />
                    ) : s === null ? (
                      <span className="text-[11px] text-stone-400">Checklist unavailable</span>
                    ) : s.steps > 0 ? (
                      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700">
                        <ListChecks size={12} />
                        {s.steps} step{s.steps === 1 ? '' : 's'} · {s.categories} categor{s.categories === 1 ? 'y' : 'ies'}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-600">
                        <CircleSlash size={12} /> No checklist yet
                      </span>
                    )}
                    <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-stone-400 group-hover:text-red-600">
                      {s && s.steps > 0 ? 'Edit' : 'Set up'} <ChevronRight size={12} />
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProvisioningDevicePicker;
